import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { adminGet, adminPost, adminPut, adminDel, authHeaders } from "@/lib/adminApi";
import { PILLAR_LABELS } from "@/lib/instant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PILLARS = Object.keys(PILLAR_LABELS);

// SOS 5 minutes : Julie gère les situations et attache à chacune SON audio dédié
// (médiathèque ou envoi direct). Cet audio accompagne les 5 minutes du SOS.
export default function Sos() {
  const [rows, setRows] = useState([]);
  const [media, setMedia] = useState([]);
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [listening, setListening] = useState(false);
  const audioRef = useRef(null);
  const fileInput = useRef(null);

  const load = () => adminGet("/sos").then((d) => setRows(d.sos || [])).catch(() => setRows([]));
  const loadMedia = () => adminGet("/media").then((d) => setMedia(d.media || [])).catch(() => {});
  useEffect(() => {
    load();
    loadMedia();
    return () => audioRef.current?.pause();
  }, []);

  const save = async () => {
    if (!form.name?.trim()) {
      toast("Donnez un nom à la situation.");
      return;
    }
    try {
      const payload = { name: form.name.trim(), pillar: form.pillar || "respirer", active: form.active !== false, sound_path: form.sound_path || null };
      if (form.id) await adminPut(`/sos/${form.id}`, payload);
      else await adminPost("/sos", payload);
      toast("Situation enregistrée.");
      setForm(null);
      load();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const remove = async (r) => {
    await adminDel(`/sos/${r.id}`).catch(() => {});
    toast("Supprimé.");
    load();
  };

  const uploadAudio = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: m } = await axios.post(`${API}/admin/media`, data, { headers: authHeaders() });
      await loadMedia();
      setForm((f) => ({ ...f, sound_path: m.storage_path }));
      toast("Audio enregistré — il accompagnera les 5 minutes de ce SOS.");
    } catch (err) {
      toast(err.response?.data?.detail || "Envoi impossible.");
    } finally {
      setUploading(false);
    }
  };

  const toggleListen = () => {
    if (listening) {
      audioRef.current?.pause();
      setListening(false);
      return;
    }
    const a = new Audio(`${API}/media/${form.sound_path}`);
    audioRef.current = a;
    a.play().then(() => setListening(true)).catch(() => toast("Lecture impossible."));
    a.onended = () => setListening(false);
  };

  const soundName = (path) => media.find((m) => m.storage_path === path)?.original_filename || path;

  return (
    <div data-testid="admin-sos-module">
      <h2 className="rav-admin-h2">SOS — 5 minutes</h2>
      <button type="button" className="rav-primary-button rav-admin-create" data-testid="sos-create" onClick={() => { setListening(false); audioRef.current?.pause(); setForm({ pillar: "respirer", active: true }); }}>
        + Ajouter
      </button>

      {form && (
        <div className="rav-admin-form" data-testid="sos-form">
          <input className="rav-input" data-testid="sos-form-name" placeholder="Situation (ex. J'ai besoin de me calmer)" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select className="rav-input" data-testid="sos-form-pillar" value={form.pillar || "respirer"} onChange={(e) => setForm({ ...form, pillar: e.target.value })}>
            {PILLARS.map((p) => (
              <option key={p} value={p}>{PILLAR_LABELS[p]}</option>
            ))}
          </select>
          <div className="rav-admin-form-row">
            <select className="rav-input" data-testid="sos-form-sound" value={form.sound_path || ""} onChange={(e) => setForm({ ...form, sound_path: e.target.value || null })}>
              <option value="">Aucun audio dédié</option>
              {media.filter((m) => m.content_type?.startsWith("audio")).map((m) => (
                <option key={m.id} value={m.storage_path}>{m.original_filename}</option>
              ))}
            </select>
            <input ref={fileInput} type="file" accept="audio/*" hidden data-testid="sos-form-sound-input" onChange={uploadAudio} />
            <button type="button" className="rav-text-link" data-testid="sos-form-sound-upload" disabled={uploading} onClick={() => fileInput.current?.click()}>
              {uploading ? "Envoi en cours…" : "Envoyer un audio"}
            </button>
            {form.sound_path && (
              <button type="button" className="rav-text-link" data-testid="sos-form-sound-listen" onClick={toggleListen}>
                {listening ? "Arrêter l'écoute" : "Écouter"}
              </button>
            )}
            {form.sound_path && (
              <button type="button" className="rav-text-link rav-note-delete" data-testid="sos-form-sound-remove" onClick={() => { audioRef.current?.pause(); setListening(false); setForm({ ...form, sound_path: null }); }}>
                Retirer l'audio
              </button>
            )}
          </div>
          <p className="rav-page-sub rav-admin-note" data-testid="sos-form-sound-hint">
            L'audio dédié accompagne les 5 minutes de ce SOS (il tourne en boucle si besoin). Sans audio dédié, l'habillage sonore habituel s'applique.
          </p>
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="sos-form-save" onClick={save}>
              Enregistrer
            </button>
            <button type="button" className="rav-text-link" data-testid="sos-form-cancel" onClick={() => { setForm(null); setListening(false); audioRef.current?.pause(); }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="rav-admin-list">
        {rows.map((r, i) => (
          <div key={r.id} className="rav-admin-row" data-testid={`sos-row-${i}`}>
            <span className="rav-admin-row-label">{r.name}</span>
            <span className="rav-admin-row-sub">
              {PILLAR_LABELS[r.pillar] || r.pillar}
              {r.sound_path ? ` · audio : ${soundName(r.sound_path)}` : " · aucun audio dédié"}
            </span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`sos-edit-${i}`} onClick={() => { setListening(false); audioRef.current?.pause(); setForm({ ...r }); }}>
                Modifier
              </button>
              <button type="button" className="rav-text-link" data-testid={`sos-delete-${i}`} onClick={() => remove(r)}>
                Supprimer
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
